<template>
  <section class="cs" aria-label="Претензионная работа">
    <header class="cs__head">
      <div>
        <h2 class="cs__title">Претензионная работа</h2>
        <p class="cs__lead">
          Исходы рейсов и простои за {{ days }} дн. — система раскладывает их сама.
          <span class="cs__hint" title="Срыв заводу — основание для претензии; сумма и срок подачи видны в разделе. Простой подтвердите — акт уйдёт заказчику сам.">?</span>
        </p>
      </div>
      <div class="cs__actions">
        <router-link class="cs__btn cs__btn--ghost" :to="{ path: '/claims', query: { report: 1 } }">Сообщить о простое</router-link>
        <router-link class="cs__btn" to="/claims">Открыть раздел →</router-link>
      </div>
    </header>

    <div v-if="error" class="cs__error">Не удалось загрузить: {{ error }}</div>
    <div v-else-if="!segments.length && pending" class="cs__skeleton"><span v-for="n in 4" :key="n"></span></div>
    <div v-else class="cs__grid">
      <router-link
        v-for="(s, index) in segments"
        :key="s.key"
        :to="{ path: '/claims', query: { s: s.key } }"
        :class="['cs__tile', `cs__tile--${s.level}`, { 'is-zero': !s.count }]"
        :style="{ '--i': index }"
        :title="s.hint"
      >
        <span class="cs__icon" aria-hidden="true">{{ icon(s.key) }}</span>
        <span class="cs__count">{{ s.count }}</span>
        <span class="cs__name">{{ s.title }}</span>
        <span v-if="subtitle(s)" class="cs__sub">{{ subtitle(s) }}</span>
      </router-link>
    </div>
  </section>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { fetchClaimsSummary } from '@/backend/claimsApi'
import { SEGMENT_ICONS, segmentSubtitle, stripSegments } from '@/pages/claims/claimsLogic.mjs'

const REFRESH_MS = 2 * 60 * 1000
const data = ref(null)
const pending = ref(false)
const error = ref('')
let timer = null

const segments = computed(() => stripSegments(data.value?.segments))
const days = computed(() => data.value?.days ?? 7)
const icon = (key) => SEGMENT_ICONS[key] || '•'
const subtitle = (s) => segmentSubtitle(s)

async function load() {
  pending.value = true
  try {
    data.value = await fetchClaimsSummary()
    error.value = ''
  } catch (e) {
    error.value = e?.message || 'нет связи'
  } finally {
    pending.value = false
  }
}

defineExpose({ load })

onMounted(() => {
  load()
  timer = setInterval(load, REFRESH_MS)
})
onBeforeUnmount(() => clearInterval(timer))
</script>

<style scoped>
.cs { --gold: #f2b705; --ink: #1f2430; --muted: #6b7280; --line: #e7e5df;
  --critical: #dc2626; --warning: #d97706; --info: #6b7a99;
  padding: 18px 20px; border-radius: 20px; background: #fff; border: 1px solid var(--line);
  box-shadow: 0 18px 40px -30px rgba(31, 36, 48, .45); display: flex; flex-direction: column; gap: 14px; }
.cs__head { display: flex; justify-content: space-between; gap: 12px; flex-wrap: wrap; align-items: flex-end; }
.cs__title { margin: 0; font-size: 20px; letter-spacing: -.01em; color: var(--ink); }
.cs__lead { margin: 4px 0 0; color: var(--muted); font-size: 13.5px; }
.cs__hint { display: inline-grid; place-items: center; width: 18px; height: 18px; margin-left: 4px; border-radius: 50%;
  font-size: 11px; font-weight: 700; color: var(--ink); background: #f3f1ea; cursor: help; }
.cs__actions { display: flex; gap: 8px; flex-wrap: wrap; }
.cs__btn { display: inline-flex; align-items: center; padding: 9px 14px; border-radius: 999px; font-size: 13.5px;
  font-weight: 600; text-decoration: none; color: var(--ink); background: linear-gradient(135deg, #ffe08a, var(--gold));
  border: 0; cursor: pointer; transition: transform .2s, box-shadow .2s; }
.cs__btn:hover { transform: translateY(-1px); box-shadow: 0 8px 18px -10px rgba(242, 183, 5, .9); }
.cs__btn--ghost { background: #f7f5ef; border: 1px solid var(--line); }
.cs__grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(190px, 1fr)); gap: 10px; }
.cs__tile { position: relative; display: grid; grid-template-columns: auto 1fr; grid-template-rows: auto auto auto;
  column-gap: 10px; padding: 12px 14px; border-radius: 16px; text-decoration: none; color: var(--ink);
  background: #fbfaf7; border: 1px solid var(--line); border-left: 4px solid var(--info);
  opacity: 0; transform: translateY(6px); animation: cs-in .45s cubic-bezier(.2, .7, .2, 1) forwards;
  animation-delay: calc(var(--i) * 45ms); transition: transform .2s, box-shadow .2s; }
.cs__tile:hover { transform: translateY(-2px); box-shadow: 0 14px 26px -18px rgba(31, 36, 48, .55); }
.cs__tile--critical { border-left-color: var(--critical); background: linear-gradient(180deg, #fff6f6, #fbfaf7); }
.cs__tile--warning { border-left-color: var(--warning); background: linear-gradient(180deg, #fff9ef, #fbfaf7); }
.cs__tile.is-zero { opacity: .75; border-left-color: #16a34a; background: #f6fbf7; }
.cs__icon { grid-row: span 2; font-size: 20px; line-height: 1.4; }
.cs__count { font-size: 24px; font-weight: 750; letter-spacing: -.02em; line-height: 1.1; }
.cs__name { grid-column: 2; font-size: 13px; color: #374151; line-height: 1.3; }
.cs__sub { grid-column: 1 / -1; margin-top: 6px; font-size: 12px; font-weight: 600; color: var(--critical); }
.cs__error { color: var(--critical); font-size: 13.5px; }
.cs__skeleton { display: grid; grid-template-columns: repeat(auto-fill, minmax(190px, 1fr)); gap: 10px; }
.cs__skeleton span { height: 76px; border-radius: 16px; background: linear-gradient(90deg, #f2f0ea, #faf9f5, #f2f0ea);
  background-size: 200% 100%; animation: cs-shimmer 1.4s linear infinite; }
@keyframes cs-in { to { opacity: 1; transform: none; } }
@keyframes cs-shimmer { to { background-position: -200% 0; } }
@media (prefers-reduced-motion: reduce) { .cs__tile { animation: none; opacity: 1; transform: none; } }
</style>
